import {
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  resendSignUpCode,
  confirmSignUp as cognitoConfirmSignUp,
  getCurrentUser,
  updatePassword,
  resetPassword,
  fetchUserAttributes,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { generatePin, cleanPhoneNumber } from '../../utils';
import { getUser } from './manage-users.service';
import { Emails } from '../lib/emails';
import { sendEmail, sendSMS } from './email.service';
import { get } from 'lodash';
import { changeUserPassword, signUpUser } from './roles.service';

export const signUp = async (estring: string, password: string) => {
  try {
    const resp = await signUpUser(estring, password);
    return resp.userSub;
  } catch (err) {
    if (err.code === 'UsernameExistsException') {
      throw new Error('Email address already exists');
    }
    if (err.code === 'InvalidPasswordException') {
      throw new Error(err.message);
    }
    throw err;
  }
};

export const signIn = async (email: string, password: string) => {
  // Amplify v6 throws UserAlreadyAuthenticatedException if a session
  // already exists. Clear it first so the second signIn call (after MFA
  // PIN entry) doesn't blow up.
  try {
    await cognitoSignOut();
  } catch (e) {
    // No existing session; continue.
  }
  try {
    const result = await cognitoSignIn({ username: email, password });
    let attributes: any = {};
    if (result.isSignedIn) {
      try {
        attributes = await fetchUserAttributes();
      } catch (e) {
        attributes = {};
      }
    }
    return { ...result, attributes };
  } catch (err) {
    const code = err.name || err.code;
    const invalidCredentials =
      code === 'UserNotFoundException' ||
      code === 'NotAuthorizedException';
    if (invalidCredentials) {
      throw new Error('That email and password combination is not valid');
    }
    throw err;
  }
};

// AER-1668: expose the Cognito/Amplify sign-out so logout() can actually end
// the session before wiping local state. clearStorage() only clears localforage
// (the redux-persist store); the Amplify Cognito tokens live in localStorage and
// survive that clear. Left valid, those tokens let the gateway's boot-time
// restoreSessionFromAmplify find a live session on the /logout boot and
// re-authenticate the just-signed-out user straight back into /dashboard. Wraps
// the same cognitoSignOut() that signIn() already uses to clear a stale session.
export const signOut = async () => {
  return await cognitoSignOut();
};

// AER-1819: the signed-in user's Cognito access token, which POST /user/deleteaccount uses to
// prove whose account is being deleted. Amplify refreshes an expired access token while the
// refresh token (30 days) is still valid. Resolves undefined when there is no live session.
export const getCognitoAccessToken = async (): Promise<string | undefined> => {
  try {
    const session = await fetchAuthSession();
    const token = get(session, 'tokens.accessToken');
    return token ? token.toString() : undefined;
  } catch (e) {
    return undefined;
  }
};

export const generatePinAndSendMsgToUser = async (
  email: string,
  useSMSIfAvail = true
) => {
  const pin = generatePin();
  const user = await getUser(email);

  const send = Emails.verify({ ToAddresses: [email], props: { pin } });

  const mfa = get(user, 'mfa', 'email');
  let method;

  if (mfa === 'sms' && useSMSIfAvail) {
    method = 'sms';
    await sendSMS({
      to: cleanPhoneNumber(get(user, 'phone')),
      body: send.Message.Body.Html.Data,
    }).catch(async error => {
      method = 'email';
      await sendEmail(send);
    });
  } else {
    method = 'email';
    await sendEmail(send);
  }

  return { pin, method };
};

export const resendCode = (emailAddress: string) =>
  resendSignUpCode({ username: emailAddress });

export const confirmSignUp = async (email: string, code: string) => {
  return await cognitoConfirmSignUp({ username: email, confirmationCode: code });
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  await getCurrentUser();
  return await updatePassword({ oldPassword, newPassword });
};

export const forgotPassword = async (username: string) => {
  try {
    return await resetPassword({ username });
  } catch (err) {
    if (get(err, 'message', '').indexOf('with error Success') > -1) {
      return Promise.resolve({
        CodeDeliveryDetails: true,
      });
    } else {
      throw err;
    }
  }
};

export const forgotPasswordSubmit = async (
  username: string,
  code: string,
  password: string
) => {
  return await changeUserPassword(username, code, password);
};
